import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

/**
 * Centered confirm dialog for duty-free flows (clear bag, replace cart, etc.).
 * Host owns open state and handles confirmed / cancelled.
 */
@Component({
  selector: 'app-dutyfree-confirm-dialog',
  templateUrl: './dutyfree-confirm-dialog.component.html',
  styleUrls: ['./dutyfree-confirm-dialog.component.scss'],
})
export class DutyfreeConfirmDialogComponent implements OnChanges {
  private static nextId = 0;
  readonly titleId = `df-confirm-title-${DutyfreeConfirmDialogComponent.nextId++}`;

  @ViewChild('dialogContainer') dialogContainer?: ElementRef<HTMLElement>;
  @ViewChild('primaryAction') primaryAction?: ElementRef<HTMLElement>;

  @Input() isOpen = false;
  @Input() title = '';
  @Input() message = '';
  @Input() confirmLabel = '';
  @Input() cancelLabel = '';
  @Input() icon = '';
  /** `alert` uses the warm duty-free danger tint; `accent` uses gold. */
  @Input() iconTone: 'alert' | 'accent' = 'alert';
  @Input() busy = false;
  @Input() cancelIsPrimary = false;

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  private previouslyFocused: HTMLElement | null = null;

  get primaryLabel(): string {
    return this.cancelIsPrimary ? this.cancelLabel : this.confirmLabel;
  }

  get secondaryLabel(): string {
    return this.cancelIsPrimary ? this.confirmLabel : this.cancelLabel;
  }

  get showSecondary(): boolean {
    return !!this.cancelLabel;
  }

  onPrimaryClick() {
    if (this.cancelIsPrimary) {
      this.onCancel();
      return;
    }
    this.onConfirm();
  }

  onSecondaryClick() {
    if (this.cancelIsPrimary) {
      this.onConfirm();
      return;
    }
    this.onCancel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) {
      return;
    }
    if (this.isOpen) {
      this.captureTriggerFocus();
      setTimeout(() => this.focusPrimaryAction());
      return;
    }
    if (!changes['isOpen'].firstChange) {
      this.restoreTriggerFocus();
    }
  }

  onDialogKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }
    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      return;
    }
    if (focusable.length === 1) {
      event.preventDefault();
      focusable[0].focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const root = this.dialogContainer?.nativeElement;

    if (event.shiftKey) {
      if (!active || active === first || !root?.contains(active)) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (!active || active === last || !root?.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  onBackdropClick() {
    if (this.busy) {
      return;
    }
    this.closeWithFocusRestore(() => this.cancelled.emit());
  }

  onConfirm() {
    if (this.busy) {
      return;
    }
    this.closeWithFocusRestore(() => this.confirmed.emit());
  }

  onCancel() {
    if (this.busy) {
      return;
    }
    this.closeWithFocusRestore(() => this.cancelled.emit());
  }

  private closeWithFocusRestore(action: () => void): void {
    action();
    this.restoreTriggerFocus();
  }

  private captureTriggerFocus(): void {
    const active = document.activeElement;
    this.previouslyFocused =
      active instanceof HTMLElement && active !== document.body
        ? active
        : null;
  }

  private focusPrimaryAction(): void {
    const target = this.getPrimaryFocusTarget();
    target?.focus();
  }

  private getPrimaryFocusTarget(): HTMLElement | null {
    const el = this.primaryAction?.nativeElement;
    if (!el) {
      return null;
    }
    if (el.tagName === 'ION-BUTTON') {
      return el.querySelector('button');
    }
    return el;
  }

  private getFocusableElements(): HTMLElement[] {
    const root = this.dialogContainer?.nativeElement;
    if (!root) {
      return [];
    }
    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null
    );
  }

  private restoreTriggerFocus(): void {
    const el = this.previouslyFocused;
    this.previouslyFocused = null;
    if (!el || !document.contains(el)) {
      return;
    }
    setTimeout(() => el.focus());
  }
}
